import {
  BarChart3,
  CalendarClock,
  Download,
  CalendarDays,
  CalendarOff,
  ClipboardCheck,
  Receipt,
  FileText,
  Gauge,
  Grid3x3,
  LayoutDashboard,
  Scale,
  Settings,
  Upload,
  Users,
  Wallet,
  UsersRound,
} from 'lucide-react';
import { atLeast, type Role } from '@cfl/shared';

/**
 * Navigation model.
 *
 * Nested to match the structure people already know: a handful of top-level
 * headings that expand, rather than one flat list. The grouping is the previous
 * system's, because that is what everybody has in their head - "my hours are under
 * Time tracking" - and renaming or re-sorting it would make a familiar app feel
 * unfamiliar for no gain.
 *
 * Items a role has no business in are hidden rather than shown disabled, and a
 * group with nothing visible in it disappears entirely.
 */

export interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** One line on what this destination is for. */
  description: string;
  visible: (role: Role) => boolean;
  /** Not built yet: shown, but marked, so the menu is honest about it. */
  pending?: boolean;
}

export interface NavGroup {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  /** A group with one destination and no children links straight to it. */
  href?: string;
  items: NavItem[];
  visible: (role: Role) => boolean;
}

const always = () => true;
const supervisor = (role: Role) => atLeast(role, 'SUPERVISOR');
const hr = (role: Role) => atLeast(role, 'HR');
const admin = (role: Role) => atLeast(role, 'ADMIN');

export const NAV: NavGroup[] = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    href: '/',
    items: [],
    visible: always,
  },
  {
    title: 'Time tracking',
    icon: CalendarClock,
    items: [
      {
        /*
         * One entry, because there is one week. The grid and the day list were
         * two items here opening the same week, and the menu could not say which
         * to pick - they are now a toggle inside the page.
         */
        title: 'My week',
        href: '/timesheet',
        icon: CalendarClock,
        description: 'Record your hours, as a grid or day by day, and submit the week.',
        visible: always,
      },
      {
        title: 'Calendar',
        href: '/timesheet/calendar',
        icon: CalendarDays,
        description: 'Your hours on a month calendar.',
        visible: always,
      },
      {
        title: 'All times',
        href: '/timesheet/all',
        icon: UsersRound,
        description: "Everybody's entries, with a CSV export.",
        visible: supervisor,
      },
      {
        title: 'My submissions',
        href: '/timesheet/submissions',
        icon: ClipboardCheck,
        description: 'Weeks you have sent for approval, and what happened to them.',
        visible: always,
      },
      {
        title: 'Export',
        href: '/timesheet/export',
        icon: Download,
        description: 'Take hours out for payroll, then close them so they cannot change.',
        visible: supervisor,
      },
    ],
    visible: always,
  },
  {
    /*
     * The previous system's "Controlling" menu, kept by that name and with that
     * membership. It groups by WHO USES IT - the people who run working time,
     * leave and the holiday calendar - rather than by what each screen technically
     * is, which is why working time sits beside the holiday calendar here and not
     * under Administration with the customers and projects.
     */
    title: 'Controlling',
    icon: Scale,
    items: [
      {
        title: 'Working time',
        href: '/reports',
        icon: CalendarClock,
        description: 'Target against worked, day by day, with the monthly statement.',
        visible: always,
      },
      {
        title: 'Absence',
        href: '/absence',
        icon: CalendarOff,
        description: 'Request leave and see what has been approved.',
        visible: always,
      },
      {
        title: 'Absence calendar',
        href: '/absence/calendar',
        icon: CalendarDays,
        description: 'Who is away this month, and who has asked to be.',
        visible: supervisor,
      },
      {
        title: 'Absence report',
        href: '/absence/report',
        icon: FileText,
        description: 'Approved leave this month, by person and type.',
        visible: supervisor,
      },
      {
        // Beside the holiday calendar rather than under Administration, because it
        // is the screen the holiday list is applied FROM.
        title: 'Working contracts',
        href: '/admin/contracts',
        icon: Scale,
        description: 'Who works which days, and who has no contract at all.',
        visible: hr,
      },
      {
        title: 'Public holidays',
        href: '/admin/setup/holidays',
        icon: CalendarDays,
        description: 'The holiday calendar each contract follows.',
        visible: hr,
      },
      {
        // The previous system's HR dashboard, by the name it does rather than the
        // department that reads it.
        title: 'Team',
        href: '/reporting/team/people',
        icon: UsersRound,
        description: "Where the month's hours went, and which days are still empty.",
        visible: supervisor,
      },
      {
        // The previous system's "missing days" view. Its own entry rather than a tab
        // two clicks in: it is the screen somebody opens deliberately to find out who
        // owes time, not one they arrive at while browsing.
        title: 'Missing days',
        href: '/reporting/team/missing',
        icon: CalendarOff,
        description: 'Working days with nothing recorded, by person.',
        visible: supervisor,
      },
    ],
    visible: always,
  },
  {
    title: 'Approvals',
    icon: ClipboardCheck,
    href: '/approvals',
    items: [],
    visible: supervisor,
  },
  {
    // One destination left now that Team sits under Controlling, so the heading
    // links straight to it rather than expanding to a list of one.
    title: 'Reporting',
    icon: BarChart3,
    items: [
      {
        title: 'Charts',
        href: '/reporting',
        icon: BarChart3,
        description: 'Hours by month, project, activity and person.',
        visible: supervisor,
      },
      {
        // The previous system's project overview, project details, monthly report
        // and accounting status - one screen, because they are the same row asked
        // at different widths.
        title: 'Project money',
        href: '/reporting/projects',
        icon: Wallet,
        description: 'What each project earned, cost, invoiced, and has left of its budget.',
        // HR, not supervisor: it is every project's margin and cost base across
        // every team, which is finance rather than approval.
        visible: hr,
      },
      {
        title: 'Year',
        href: '/reporting/year',
        icon: Grid3x3,
        description: 'Every month of the year, person by person.',
        visible: supervisor,
      },
    ],
    visible: supervisor,
  },
  {
    // Its own heading rather than a child of Administration: raising an invoice is
    // routine work for whoever bills, not a configuration task.
    title: 'Invoices',
    icon: Receipt,
    href: '/invoices',
    items: [],
    visible: hr,
  },
  {
    // Everybody, not just HR: an expense is somebody's own record, and the list
    // is scoped so each role sees what it should.
    title: 'Expenses',
    icon: Wallet,
    href: '/expenses',
    items: [],
    visible: () => true,
  },
  {
    title: 'Administration',
    icon: Users,
    items: [
      {
        title: 'People',
        href: '/admin/people',
        icon: Users,
        description: 'Employees, working contracts and teams.',
        visible: hr,
      },
      {
        title: 'Import',
        href: '/import',
        icon: Upload,
        description: 'Load recorded time from a spreadsheet.',
        visible: hr,
      },
      {
        title: 'Setup',
        // The explicit tab, not the bare path: '/admin/setup' is a prefix of
        // '/admin/setup/holidays', which lives under Controlling, and the group
        // that contains the current route is computed by prefix - so the bare
        // path would spring open BOTH groups whenever holidays was showing.
        href: '/admin/setup/customers',
        icon: Settings,
        description: 'Customers, projects, activities and public holidays.',
        visible: admin,
      },
      {
        // Its own entry rather than a tab on System: it is read when something
        // looks wrong, which is not while configuring anything.
        title: 'Audit',
        href: '/admin/audit',
        icon: FileText,
        description: 'Who changed something that was not their own.',
        visible: admin,
      },
      {
        title: 'System',
        href: '/admin/system',
        icon: Settings,
        description: 'How the system behaves, and what it is running on.',
        visible: admin,
      },
    ],
    visible: hr,
  },
];

/** The groups this role can see, with hidden items and empty groups dropped. */
export function visibleNav(role: Role): NavGroup[] {
  return NAV.filter((group) => group.visible(role))
    .map((group) => ({ ...group, items: group.items.filter((item) => item.visible(role)) }))
    // A group that is only a heading, with everything under it hidden, is noise.
    .filter((group) => group.href !== undefined || group.items.length > 0);
}

/** Every reachable destination, for matching the current route. */
export function allNavItems(role: Role): NavItem[] {
  return visibleNav(role).flatMap((g) => g.items);
}

export { FileText };
